import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, ClipboardList, Eye, Loader2, Pencil, Send } from 'lucide-react';
import { formConfigAPI } from '../../services/api';
import { toast } from '../ui/use-toast';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiError, labelOf, useFormConfig, useFormMeta } from '../Automation/formMeta';

/**
 * 2026-09-24 (Docs/RELEASE_NOTES_2026-09-24_FORMS.md): what this
 * contact has told the bot -- registration status, the question currently
 * waiting for an answer, every answered field with its history -- plus the
 * agent actions: fill in / correct an answer, send a form again,
 * mark registered. Opened from the Conversations header, same pattern as
 * the Pre-chat Info panel.
 */

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'outline'> = {
  completed: 'success',
  in_progress: 'warning',
  declined: 'secondary',
  never_asked: 'outline',
};

const show = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    if ('attachment_id' in value) return `file #${value.attachment_id}`;
    if ('lat' in value && 'lng' in value) return `${value.lat}, ${value.lng}`;
    if ('address' in value) return String(value.address);
    return JSON.stringify(value);
  }
  return String(value);
};

const when = (iso?: string | null) => (iso ? new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleString() : '');

const ContactFormsPanel: React.FC<{ contactId: number }> = ({ contactId }) => {
  const meta = useFormMeta();
  const cfg = useFormConfig();
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);
  const [sendKey, setSendKey] = useState('');

  const load = useCallback(() => {
    formConfigAPI.getContact(contactId).then(setData).catch(() => setData({ error: true }));
  }, [contactId]);
  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const act = async (fn: () => Promise<any>, success: string): Promise<boolean> => {
    setBusy(true);
    try {
      setData(await fn());
      toast({ title: success });
      return true;
    } catch (err) {
      toast({ title: 'Not done', description: apiError(err, 'Something went wrong'), variant: 'destructive' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="px-4 py-3">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (data.error) return <div className="px-4 py-3 text-xs text-destructive">Could not load answers.</div>;

  const reg = data.registration || {};
  const active = data.active_session;
  const answers = Object.entries(data.answers || {}) as Array<[string, any]>;
  const liveSets = (cfg?.forms || []).filter((s) => !s.archived_at);
  const fieldLabel = (key: string) => (cfg?.fields || []).find((f) => f.key === key)?.label || key;

  return (
    <div className="space-y-3 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <ClipboardList className="h-3.5 w-3.5" /> {reg.form_name || 'Registration'}
        </p>
        <Badge variant={STATUS_VARIANT[reg.status] || 'outline'} className="text-[10px]">
          {String(reg.status || 'never_asked').replace(/_/g, ' ')}
        </Badge>
        {reg.detail?.completed_at && <span className="text-[11px] text-muted-foreground">{when(reg.detail.completed_at)}</span>}
        {reg.status !== 'completed' && reg.detail?.missing_fields?.length > 0 && (
          <span className="text-[11px] text-muted-foreground">missing: {reg.detail.missing_fields.map(fieldLabel).join(', ')}</span>
        )}
        <div className="ml-auto flex gap-1">
          {reg.form_key && reg.status !== 'completed' && (
            <Button
              variant="outline" size="sm" className="h-7 text-xs" disabled={busy || !!active}
              onClick={() => act(() => formConfigAPI.sendFormToContact(contactId, reg.form_key), 'Registration form sent')}
            >
              <Send className="mr-1 h-3 w-3" /> Send
            </Button>
          )}
          {reg.status !== 'completed' && (
            <Button
              variant="outline" size="sm" className="h-7 text-xs" disabled={busy}
              onClick={() => window.confirm('Mark this contact as registered?') &&
                act(() => formConfigAPI.markRegistered(contactId), 'Marked as registered')}
            >
              <CheckCircle2 className="mr-1 h-3 w-3" /> Mark registered
            </Button>
          )}
        </div>
      </div>

      {active && (
        <div className="rounded-md border border-amber-300/60 bg-amber-50/60 px-3 py-2 text-xs dark:bg-amber-950/20">
          Waiting for an answer: <b>{fieldLabel(active.current_field)}</b> ({active.answered}/{active.total}) ·{' '}
          {labelOf(meta?.enforcements, active.enforcement)}
          {active.form_key && <> · {liveSets.find((s) => s.key === active.form_key)?.name || active.form_key}</>}
          {active.expires_at && <> · gives up {when(active.expires_at)}</>}
        </div>
      )}

      <div className="divide-y rounded-md border">
        {answers.length === 0 && <p className="p-3 text-xs text-muted-foreground">No answers yet.</p>}
        {answers.map(([key, a]) => {
          const hidden = a.sensitive && !revealed[key];
          return (
            <div key={key} className="px-3 py-2 text-sm">
              <div className="flex items-center gap-2">
                <button
                  type="button" className="text-muted-foreground"
                  onClick={() => setOpenHistory(openHistory === key ? null : key)}
                  aria-label="Show history"
                >
                  {openHistory === key ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
                <span className="w-32 shrink-0 text-xs text-muted-foreground">{a.label}</span>
                {editing?.key === key ? (
                  <>
                    <Input
                      className="h-7 flex-1 text-xs" autoFocus value={editing.value}
                      onChange={(e) => setEditing({ key, value: e.target.value })}
                    />
                    <Button
                      size="sm" className="h-7 text-xs" disabled={busy}
                      onClick={() => act(() => formConfigAPI.fillAnswer(contactId, key, editing.value), 'Answer saved')
                        .then((ok) => ok && setEditing(null))}
                    >
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditing(null)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 truncate font-medium">{hidden ? '••••••' : show(a.value)}</span>
                    {a.sensitive && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setRevealed({ ...revealed, [key]: !revealed[key] })} title="Show">
                        <Eye className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost" size="sm" className="h-6 w-6 p-0" title="Fill in / correct"
                      onClick={() => setEditing({ key, value: typeof a.value === 'object' && a.value !== null ? '' : show(a.value) === '—' ? '' : show(a.value) })}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
              {openHistory === key && (
                <div className="mt-1.5 space-y-1 border-l-2 pl-3 ml-1.5">
                  {(a.history || []).length === 0 && <p className="text-[11px] text-muted-foreground">No history.</p>}
                  {(a.history || []).map((h: any, i: number) => (
                    <div key={i} className="flex justify-between gap-2 text-[11px]">
                      <span>
                        {hidden ? '••••••' : show(h.value)}{' '}
                        <span className="text-muted-foreground">({h.status}{h.via && h.via !== 'chat' ? ` · ${h.via}` : ''})</span>
                      </span>
                      <span className="text-muted-foreground">{when(h.at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(cfg?.fields || []).some((f) => !(data.answers || {})[f.key]) && (
        <div className="flex items-center gap-2">
          <Select value="" onValueChange={(key) => setEditing({ key, value: '' })}>
            <SelectTrigger className="h-7 w-48 text-xs"><SelectValue placeholder="Fill in another field…" /></SelectTrigger>
            <SelectContent>
              {(cfg?.fields || []).filter((f) => !(data.answers || {})[f.key]).map((f) => (
                <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {editing && !(data.answers || {})[editing.key] && (
            <>
              <Input className="h-7 flex-1 text-xs" autoFocus value={editing.value} placeholder={fieldLabel(editing.key)}
                onChange={(e) => setEditing({ key: editing.key, value: e.target.value })} />
              <Button size="sm" className="h-7 text-xs" disabled={busy || !editing.value}
                onClick={() => act(() => formConfigAPI.fillAnswer(contactId, editing.key, editing.value), 'Answer saved').then((ok) => ok && setEditing(null))}>
                Save
              </Button>
            </>
          )}
        </div>
      )}

      {liveSets.length > 0 && (
        <div className="flex items-center gap-2 border-t pt-2">
          <Select value={sendKey} onValueChange={setSendKey}>
            <SelectTrigger className="h-7 w-56 text-xs"><SelectValue placeholder="Send a form…" /></SelectTrigger>
            <SelectContent>
              {liveSets.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.name} · {labelOf(meta?.form_kinds, s.kind)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm" className="h-7 text-xs" disabled={!sendKey || busy || !!active}
            onClick={() => act(() => formConfigAPI.sendFormToContact(contactId, sendKey), 'Form sent')}
          >
            <Send className="mr-1 h-3 w-3" /> Send
          </Button>
          {active && <span className="text-[11px] text-muted-foreground">Wait for the current form to finish first.</span>}
        </div>
      )}
    </div>
  );
};

export default ContactFormsPanel;
